// Boost Blenz Admin — Forecasting (Overview → under Decision Dashboard).
// "Units Sold": physical jars shipped per Week / Month / Year, split by jar type, for
// production + inventory planning. Reads /api/units (Shopify net_items_sold per SKU —
// counts every jar shipped incl. kit components + free reships). Summary-first: run-rate
// tiles → stacked bar chart → expandable per-period table. Jar lists (tooltip + row
// detail) are sorted by quantity, highest first. Only the in-progress period says "so far".
import React from "react";
import { OSWALD, FIGTREE, useT, useIsMobile } from "../theme";
import { Icon } from "../atoms";
import { useCachedJson } from "../datacache";

// Jar palette (owner-chosen): Parasite red, Heavy Metal green, Liver yellow, Kidney
// blue, Binder a palette gray (its real black would muddy the chart). Fixed order = the
// stacked-bar band order (kept stable across bars so the eye can track a color).
const JARS: { sku: string; name: string; color: string }[] = [
  { sku: "30_Day_Parasite_BB", name: "Parasite", color: "#ee5a6a" },
  { sku: "30_Day_Metal_BB", name: "Heavy Metal", color: "#5fd39a" },
  { sku: "30_Day_Liver_BB", name: "Liver", color: "#f5c451" },
  { sku: "30_Day_Kidney_BB", name: "Kidney", color: "#6aa9e0" },
  { sku: "Binder_Caps_BB", name: "Binder", color: "#b2aac6" },
];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const C = {
  card: "linear-gradient(180deg,rgba(26,20,54,.75),rgba(18,13,40,.72))",
  cell: "#171232", line: "rgba(150,130,230,.20)", lineSoft: "rgba(150,130,230,.10)",
  ink: "#f4f1ff", body: "#e6dfff", muted: "#a99fd0", faint: "#7a6fa8",
  orange: "#f8925c", green: "#5fd39a", red: "#ff9f8a",
};
const int = (n: number) => Math.round(n).toLocaleString("en-US");
const YOY_FLOOR = 300; // below this many jars last year, a % is launch-ramp noise → show the count instead

type Period = {
  key: string; label: string; partial: boolean;
  jars: Record<string, number>; total: number;
  yoyTotal?: number | null; yoyJars?: Record<string, number> | null;
};
type Grain = "week" | "month" | "year";

// Jars sorted by this period's quantity, highest first (for tooltip + row detail).
const sortedJars = (p: Period) => JARS.slice().sort((a, b) => (p.jars[b.sku] || 0) - (p.jars[a.sku] || 0));
// "Jul '25" for the same month a year before a given YYYY-MM-01 key.
const prevYearLabel = (key: string) => `${MON[parseInt(key.slice(5, 7), 10) - 1]} '${String((+key.slice(0, 4) - 1)).slice(2)}`;

export function ForecastingView() {
  const t = useT();
  const isMobile = useIsMobile();
  const [grain, setGrain] = React.useState<Grain>("month");
  const [open, setOpen] = React.useState<Record<string, boolean>>({});
  const [hover, setHover] = React.useState<number | null>(null);

  const { data, loading } = useCachedJson<any>(`/api/units?grain=${grain}`);
  const all: Period[] = data?.periods || [];
  const shown = grain === "year" ? all : all.slice(-12);
  const per = grain === "week" ? "week" : grain === "month" ? "month" : "year";

  const max = Math.max(1, ...shown.map((p) => p.total));
  const complete = shown.filter((p) => !p.partial);

  // ---- forecasting tiles (summary before detail) ----
  const tiles: { lab: string; big: string; unit?: string; cap: React.ReactNode; hero?: boolean }[] = [];
  if (grain === "year") {
    const cur = shown[shown.length - 1];
    const prevFull = complete[complete.length - 1];
    const now = new Date();
    const start = Date.UTC(now.getUTCFullYear(), 0, 1);
    const frac = Math.max(0.02, (Date.now() - start) / (365 * 864e5));
    const pace = cur ? cur.total / frac : 0;
    const allTime = shown.reduce((s, p) => s + p.total, 0);
    tiles.push(
      { lab: `${cur?.label || ""} so far`, big: int(cur?.total || 0), unit: "jars", hero: true, cap: `On pace for ~${int(Math.round(pace / 1000) * 1000)} this year` },
      { lab: prevFull ? `${prevFull.label} full year` : "Last full year", big: prevFull ? int(prevFull.total) : "—", unit: "jars", cap: prevFull && cur ? yoyPhrase(cur.total, prevFull.total, true) : "—" },
      { lab: "All-time", big: int(allTime), unit: "jars", cap: "Every jar shipped since launch" },
    );
  } else {
    const N = grain === "week" ? 8 : 3;
    const recent = complete.slice(-N);
    const runRate = recent.length ? recent.reduce((s, p) => s + p.total, 0) / recent.length : 0;
    const prior = complete.slice(-N - 1, -1);
    const priorAvg = prior.length ? prior.reduce((s, p) => s + p.total, 0) / prior.length : runRate;
    const latest = complete[complete.length - 1];
    const peak = Math.max(0, ...shown.map((p) => p.total));
    tiles.push(
      { lab: `${per}ly run rate`, big: int(runRate), unit: `jars/${per}`, hero: true, cap: `Make about this many per ${per} to hold inventory (${N}-${per} avg)` },
      { lab: `Latest full ${per}`, big: latest ? int(latest.total) : "—", cap: latest ? <>{latest.label} · {yoyPhrase(runRate, priorAvg, false, `vs prior ${N}`)}</> : "—" },
      { lab: `${grain === "week" ? "12-week" : "12-month"} peak`, big: int(peak), cap: `Your busiest ${per} — size buffer stock to this` },
    );
  }

  const tabBtn = (g: Grain, lbl: string) => (
    <span key={g} onClick={() => { setGrain(g); setOpen({}); setHover(null); }}
      style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.08em", fontSize: 12, padding: "9px 20px", borderRadius: 999, cursor: "pointer", textTransform: "uppercase", background: grain === g ? "#f4f1ff" : "transparent", color: grain === g ? "#1a1330" : C.muted }}>{lbl}</span>
  );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", fontSize: isMobile ? 24 : 32, color: t.fg }}>Units Sold</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5 }}>Physical jars shipped — for production &amp; inventory planning. Counts every jar that leaves the door: kits, singles, combos, and free replacements.</div>
      </div>

      <div style={{ border: "1px solid " + C.line, borderRadius: 20, background: C.card, overflow: "hidden" }}>
        {/* toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "16px 18px", borderBottom: "1px solid " + C.line }}>
          <div style={{ display: "flex", gap: 3, background: "rgba(13,10,30,.6)", border: "1px solid " + C.line, borderRadius: 999, padding: 4 }}>
            {tabBtn("week", "Week")}{tabBtn("month", "Month")}{tabBtn("year", "Year")}
          </div>
          <div style={{ marginLeft: "auto", fontSize: 12, color: C.faint }}>Newest first · <b style={{ color: C.muted, fontWeight: 600 }}>{grain}ly</b></div>
        </div>

        {/* stat tiles */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 1, background: C.lineSoft, borderBottom: "1px solid " + C.line }}>
          {tiles.map((ti, i) => (
            <div key={i} style={{ background: ti.hero ? "linear-gradient(180deg,rgba(248,146,92,.13),rgba(248,146,92,.05))" : C.cell, padding: "18px 20px" }}>
              <div style={{ fontFamily: OSWALD, textTransform: "uppercase", letterSpacing: "0.09em", fontSize: 11, color: ti.hero ? C.orange : C.faint, fontWeight: 700 }}>{ti.lab}</div>
              <div style={{ fontSize: 33, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums", lineHeight: 1.05, marginTop: 7, letterSpacing: "-0.5px" }}>
                {ti.big}{ti.unit && <small style={{ fontSize: 15, color: C.muted, fontWeight: 600, marginLeft: 5 }}>{ti.unit}</small>}
              </div>
              <div style={{ fontSize: 12.5, color: C.muted, marginTop: 5 }}>{ti.cap}</div>
            </div>
          ))}
        </div>

        {/* chart */}
        <div style={{ padding: "20px 20px 6px" }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
            {JARS.map((j) => (
              <span key={j.sku} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: C.body }}>
                <i style={{ width: 11, height: 11, borderRadius: 3, background: j.color, flex: "0 0 auto" }} />{j.name}
              </span>
            ))}
          </div>
          <div style={{ position: "relative", height: 250, display: "flex", alignItems: "flex-end", gap: 10, paddingTop: 6, borderBottom: "1px solid " + C.line, touchAction: "pan-y" }}
            onTouchStart={(e) => { const p = e.touches[0]; if (!p || !shown.length) return; const r = e.currentTarget.getBoundingClientRect(); setHover(Math.max(0, Math.min(shown.length - 1, Math.floor(((p.clientX - r.left) / r.width) * shown.length)))); }}
            onTouchMove={(e) => { const p = e.touches[0]; if (!p || !shown.length) return; const r = e.currentTarget.getBoundingClientRect(); setHover(Math.max(0, Math.min(shown.length - 1, Math.floor(((p.clientX - r.left) / r.width) * shown.length)))); }}>
            {loading && !shown.length ? (
              <div style={{ margin: "auto", color: C.faint, fontSize: 13 }}>Loading…</div>
            ) : shown.map((p, i) => (
              <div key={p.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover((h) => (h === i ? null : h))}
                style={{ flex: "1 1 0", display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%", cursor: "pointer", position: "relative", minWidth: 0 }}>
                {hover === i && <ChartTip p={p} align={i < 2 ? "left" : i > shown.length - 3 ? "right" : "center"} grain={grain} />}
                <div style={{ display: "flex", flexDirection: "column-reverse", borderRadius: "5px 5px 0 0", overflow: "hidden", height: Math.max(2, (p.total / max) * 100) + "%", opacity: p.partial ? 0.6 : 1, outline: p.partial ? "1px dashed rgba(255,255,255,.28)" : "none", outlineOffset: -1, filter: hover === i ? "brightness(1.12)" : "none" }}>
                  {JARS.map((j) => {
                    const v = p.jars[j.sku] || 0; if (!v) return null;
                    return <div key={j.sku} style={{ width: "100%", height: (v / p.total) * 100 + "%", background: j.color }} />;
                  })}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 9 }}>
            {shown.map((p) => (
              <div key={p.key} style={{ flex: "1 1 0", textAlign: "center", fontSize: 11.5, color: C.faint, fontVariantNumeric: "tabular-nums", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}{p.partial ? " •" : ""}</div>
            ))}
          </div>
        </div>

        {/* table */}
        <div style={{ borderTop: "1px solid " + C.line }}>
          <div style={{ display: "grid", gridTemplateColumns: cols(grain, isMobile), columnGap: isMobile ? 12 : 22, padding: "11px 20px", fontFamily: OSWALD, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 11, color: C.faint, fontWeight: 700, borderBottom: "1px solid " + C.line }}>
            <div>{grain === "week" ? "Week of" : grain === "month" ? "Month" : "Year"}</div>
            <div style={{ textAlign: "right" }}>Total jars</div>
            {grain === "month" && <div style={{ textAlign: "right" }}>vs last yr</div>}
            {!isMobile && <div style={{ textAlign: "right" }}>Mix</div>}
            <div />
          </div>
          {shown.slice().reverse().map((p) => {
            const isOpen = !!open[p.key];
            return (
              <div key={p.key}>
                <div role="button" tabIndex={0} onClick={() => setOpen((o) => ({ ...o, [p.key]: !o[p.key] }))}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => ({ ...o, [p.key]: !o[p.key] })); } }}
                  style={{ display: "grid", gridTemplateColumns: cols(grain, isMobile), columnGap: isMobile ? 12 : 22, alignItems: "center", padding: "15px 20px", borderBottom: "1px solid " + C.lineSoft, cursor: "pointer" }}>
                  <div style={{ fontWeight: 600, color: C.ink, fontSize: 15, display: "flex", alignItems: "center", gap: 9 }}>
                    {p.label}{p.partial && <span style={{ fontFamily: OSWALD, fontSize: 9.5, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 7px", borderRadius: 6, background: "rgba(248,146,92,.16)", color: C.orange, fontWeight: 700 }}>so far</span>}
                  </div>
                  <div style={{ textAlign: "right", fontSize: 19, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{int(p.total)}<small style={{ fontSize: 12, color: C.faint, fontWeight: 500, marginLeft: 5 }}>jars</small></div>
                  {grain === "month" && <div style={{ textAlign: "right" }}>{yoyCell(p)}</div>}
                  {!isMobile && <div><div style={{ height: 9, borderRadius: 5, overflow: "hidden", display: "flex", background: "rgba(255,255,255,.05)" }}>{JARS.map((j) => <i key={j.sku} style={{ height: "100%", width: (p.jars[j.sku] || 0) / (p.total || 1) * 100 + "%", background: j.color }} />)}</div></div>}
                  <div style={{ justifySelf: "end", color: isOpen ? C.body : C.faint, transition: "transform .18s", transform: isOpen ? "rotate(90deg)" : "none" }}><Icon name="chevron" size={14} color={isOpen ? C.body : C.faint} /></div>
                </div>
                {isOpen && (
                  <div style={{ background: "rgba(9,7,22,.5)", borderBottom: "1px solid " + C.lineSoft, padding: "4px 20px 14px" }}>
                    {sortedJars(p).map((j) => {
                      const v = p.jars[j.sku] || 0, pct = p.total ? (v / p.total) * 100 : 0;
                      const ly = grain === "month" && p.yoyJars ? p.yoyJars[j.sku] : null;
                      return (
                        <div key={j.sku} style={{ display: "grid", gridTemplateColumns: isMobile ? "116px 66px 1fr 46px" : "160px 90px 1fr 60px" + (grain === "month" ? " 96px" : ""), alignItems: "center", gap: isMobile ? 12 : 20, padding: "8px 0", borderBottom: "1px solid " + C.lineSoft }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: C.body }}><i style={{ width: 11, height: 11, borderRadius: 3, background: j.color, flex: "0 0 auto" }} />{j.name}</div>
                          <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{int(v)}</div>
                          <div style={{ height: 8, borderRadius: 5, background: "rgba(255,255,255,.05)", overflow: "hidden" }}><i style={{ display: "block", height: "100%", width: pct + "%", background: j.color }} /></div>
                          <div style={{ textAlign: "right", fontSize: 12.5, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{pct.toFixed(0)}%</div>
                          {grain === "month" && !isMobile && <div style={{ textAlign: "right", fontSize: 12.5, color: C.faint, fontVariantNumeric: "tabular-nums" }}>{ly ? `${int(ly)} last yr` : "—"}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {!loading && !shown.length && <div style={{ padding: "40px 20px", textAlign: "center", color: C.muted, fontSize: 14 }}>{data && data.ok === false ? "Couldn’t load units data." : "No data yet."}</div>}
        </div>
      </div>
    </div>
  );
}

// grid column template per grain (adds the YoY column on month; drops Mix on mobile).
function cols(grain: Grain, mobile: boolean) {
  if (mobile) return grain === "month" ? "1fr 96px 78px 34px" : "1fr 110px 34px";
  return grain === "month" ? "1fr 150px 96px 130px 40px" : "1fr 150px 130px 40px";
}

// "+32% vs last year" phrasing for a tile caption.
function yoyPhrase(cur: number, prev: number, yearly: boolean, suffix = "vs last year") {
  if (!prev) return "—";
  const d = Math.round((cur / prev - 1) * 100);
  const up = d >= 0;
  return <span style={{ color: up ? C.green : C.red }}>{up ? "▲" : "▼"} {Math.abs(d)}% {yearly ? "vs last year" : suffix}</span>;
}

// The "vs last yr" table cell for a month row: a % delta for complete months, or the
// last-year target for the in-progress month (a partial % would mislead).
function yoyCell(p: Period) {
  const ly = p.yoyTotal;
  if (ly == null || ly === 0) return <span style={{ color: C.faint, fontSize: 13 }}>—</span>;
  // Current (partial) month, or a small launch-ramp baseline → show last year's COUNT as
  // the planning anchor rather than a misleading percentage (avoids the ÷0 / 30,000% cases).
  if (p.partial || ly < YOY_FLOOR) return <span style={{ color: C.muted, fontSize: 12.5, fontVariantNumeric: "tabular-nums" }} title={`${prevYearLabel(p.key)}: ${int(ly)} jars`}>{int(ly)} last yr</span>;
  const d = Math.round((p.total / ly - 1) * 100);
  const up = d >= 0;
  return <span title={`${prevYearLabel(p.key)}: ${int(ly)} jars`} style={{ color: up ? C.green : C.red, fontWeight: 700, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{up ? "▲" : "▼"} {Math.abs(d)}%</span>;
}

// Hover tooltip over a chart bar — jar rows sorted by quantity, highest first.
function ChartTip({ p, align, grain }: { p: Period; align: "left" | "center" | "right"; grain: Grain }) {
  const left = align === "left" ? "0" : align === "right" ? "auto" : "50%";
  const right = align === "right" ? "0" : "auto";
  const tx = align === "center" ? "translateX(-50%)" : "none";
  return (
    <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left, right, transform: tx, zIndex: 20, background: "#0c0920", border: "1px solid " + C.line, borderRadius: 12, padding: "11px 13px", minWidth: 178, boxShadow: "0 12px 34px rgba(0,0,0,.5)", pointerEvents: "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginBottom: 8, fontFamily: OSWALD, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 12, color: C.ink }}>
        <span>{p.label}{p.partial ? " (so far)" : ""}</span><b style={{ color: C.orange }}>{int(p.total)}</b>
      </div>
      {sortedJars(p).map((j) => (
        <div key={j.sku} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, margin: "3px 0", color: C.body, fontVariantNumeric: "tabular-nums" }}>
          <i style={{ width: 11, height: 11, borderRadius: 3, background: j.color, flex: "0 0 auto" }} />
          <span style={{ flex: 1 }}>{j.name}</span><span style={{ color: C.ink, fontWeight: 600 }}>{int(p.jars[j.sku] || 0)}</span>
        </div>
      ))}
      {grain === "month" && p.yoyTotal != null && <div style={{ marginTop: 7, paddingTop: 7, borderTop: "1px solid " + C.lineSoft, fontSize: 11.5, color: C.faint }}>{prevYearLabel(p.key)}: {int(p.yoyTotal)} jars</div>}
    </div>
  );
}
